import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Skeleton } from '@/components/ui/skeleton';
import { useReportFieldMetadata, useExecuteReport, useSavedViews } from 'hooks/useAssetReports';
import {
  ReportDimension,
  ReportMeasure,
  ReportViewType,
  ChartType,
  ChartReportResponse,
  PivotReportResponse,
  AssetReportView,
  ReportConfig,
  AssetFilterCriteria,
  AssetReportFilterOperator,
  AssetReportLogicalOperator,
} from 'shared/types/asset-report';

/** Transform pivot response into stacked chart data */
function pivotToStackedChart(pivot: PivotReportResponse): ChartReportResponse {
  if (!pivot?.rows?.length) return { labels: [], datasets: [] };

  // Row keys become x-axis labels, column keys become dataset series
  const labels = pivot.rows.map(r => Object.values(r.rowKey).join(' - ') || '(Leeg)');
  const colKeys = (pivot.columnKeys || []).filter(k => k !== '__total');
  const measureLabel = pivot.rows[0]?.totals ? Object.keys(pivot.rows[0].totals)[0] : 'Aantal';

  if (colKeys.length === 0) {
    // No column dimensions — single dataset
    return {
      labels,
      datasets: [{ label: measureLabel, data: pivot.rows.map(r => r.totals[measureLabel] || 0) }],
    };
  }

  // One dataset per column key (stacked segments)
  const datasets = colKeys.map(colKey => ({
    label: colKey,
    data: pivot.rows.map(r => r.columns[colKey]?.[measureLabel] || 0),
  }));

  return { labels, datasets };
}
import ReportToolbar from './components/ReportToolbar';
import ChartView from './components/ChartView';
import PivotTableView from './components/PivotTableView';
import AssetFilterPanel from './components/AssetFilterPanel';
import SaveViewDialog from './components/SaveViewDialog';
import SavedViewsBar from './components/SavedViewsBar';

const DEFAULT_MEASURES: ReportMeasure[] = [
  { field: '__count', aggregation: 'count', label: 'Aantal' },
];

const AssetStatisticsTab: React.FC = () => {
  // Report state — rowDimensions is an array for hierarchical drill-down
  const [viewType, setViewTypeRaw] = useState<ReportViewType>('chart');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [rowDimensions, setRowDimensions] = useState<ReportDimension[]>([
    { field: 'assetCategory.name', label: 'Categorie' },
  ]);
  const [columnDimensions, setColumnDimensions] = useState<ReportDimension[]>([]);
  const [stackDimension, setStackDimension] = useState<ReportDimension | null>(null);
  const [measures, setMeasures] = useState<ReportMeasure[]>(DEFAULT_MEASURES);
  const [chartSort, setChartSort] = useState<'none' | 'asc' | 'desc'>('none');
  const [activeFilters, setActiveFilters] = useState<{
    id: string; field: string; label: string;
    operator: AssetReportFilterOperator; operatorLabel: string; value: string;
  }[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [activeViewId, setActiveViewId] = useState<number | null>(null);

  // Reset dimensions when switching view types
  const setViewType = useCallback((vt: ReportViewType) => {
    setViewTypeRaw(vt);
    if (vt === 'chart') {
      // Chart: reset to single default dimension, clear columns
      setRowDimensions([{ field: 'assetCategory.name', label: 'Voorziening > Categorie' }]);
      setColumnDimensions([]);
    } else {
      // Pivot: start with default dimension, user drills from there
      setRowDimensions([{ field: 'assetCategory.name', label: 'Voorziening > Categorie' }]);
      setColumnDimensions([]);
    }
    setActiveViewId(null);
  }, []);

  // Hooks
  const { fieldMetadata, isLoading: isLoadingMeta } = useReportFieldMetadata();
  const { data: reportData, isLoading: isLoadingReport, error: reportError, execute } = useExecuteReport();
  const { views: savedViews, saveView, deleteView } = useSavedViews();

  // Debounce timer
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Execute query on config change (debounced)
  useEffect(() => {
    if (measures.length === 0) return;
    // Chart mode requires at least one dimension; pivot mode allows empty (shows just Totaal)
    if (viewType === 'chart' && rowDimensions.length === 0) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // When stack dimension is set in chart mode, use pivot query to get cross-tab data
      const hasStack = viewType === 'chart' && stackDimension;
      const effectiveColDims = hasStack ? [stackDimension] : columnDimensions;

      // Build filter criteria from active filters
      const filterCriteria: AssetFilterCriteria | null = activeFilters.length > 0
        ? {
            version: '1.0',
            root: {
              logicalOperator: AssetReportLogicalOperator.AND,
              conditions: activeFilters.map(f => ({
                field: f.field,
                operator: f.operator,
                value: f.value || null,
              })),
            },
          }
        : null;

      const config: ReportConfig = {
        viewType: hasStack ? 'pivot' : viewType,
        chartType,
        rowDimensions,
        columnDimensions: effectiveColDims,
        measures,
        filterCriteria,
      };
      execute(config);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [viewType, chartType, rowDimensions, columnDimensions, stackDimension, measures, activeFilters, execute]);

  // Show error toast
  useEffect(() => {
    if (reportError) {
      toast.error(reportError);
    }
  }, [reportError]);

  // Pivot drill-down: add a new row dimension at a specific depth
  const handleAddRowDimension = useCallback((dim: ReportDimension, atIndex?: number) => {
    setRowDimensions(prev => {
      const idx = atIndex !== undefined ? atIndex + 1 : prev.length;
      // Remove any dimensions after the insertion point, then add new one
      const newDims = [...prev.slice(0, idx), dim];
      return newDims;
    });
  }, []);

  // Remove row dimension at index (and all deeper levels)
  const handleRemoveRowDimension = useCallback((atIndex: number) => {
    setRowDimensions(prev => prev.slice(0, atIndex));
  }, []);

  // Add column dimension for pivot — appends deeper level (like Odoo drill-down)
  const handleAddColumnDimension = useCallback((dim: ReportDimension) => {
    setColumnDimensions(prev => [...prev, dim]);
  }, []);

  // Remove column dimension at index (and all deeper levels)
  const handleRemoveColumnDimension = useCallback((atIndex: number) => {
    setColumnDimensions(prev => prev.slice(0, atIndex));
  }, []);

  // Load saved view
  const handleLoadView = useCallback((view: AssetReportView) => {
    setViewType(view.viewType);
    if (view.chartType) setChartType(view.chartType);
    setRowDimensions(view.rowDimensions.length > 0 ? view.rowDimensions : [{ field: 'assetCategory.name', label: 'Categorie' }]);
    setColumnDimensions(view.columnDimensions || []);
    setMeasures(view.measures.length > 0 ? view.measures : DEFAULT_MEASURES);
    setActiveViewId(view.id);
  }, []);

  // Save current config as view
  const handleSaveView = useCallback(async (name: string, isShared: boolean) => {
    const result = await saveView({
      name,
      isShared,
      viewType,
      chartType: viewType === 'chart' ? chartType : null,
      rowDimensions,
      columnDimensions,
      measures,
      filterCriteria: null,
      sortOrder: 0,
    });
    if (result) {
      toast.success('Weergave opgeslagen');
      setSaveDialogOpen(false);
    } else {
      toast.error('Fout bij opslaan weergave');
    }
  }, [saveView, viewType, chartType, rowDimensions, columnDimensions, measures]);

  // Handle delete view
  const handleDeleteView = useCallback(async (id: number) => {
    const ok = await deleteView(id);
    if (ok) {
      toast.success('Weergave verwijderd');
    }
  }, [deleteView]);

  // Add dimension from filter panel (replaces first row dim)
  const handleAddDimension = useCallback((dim: ReportDimension) => {
    setRowDimensions([dim]);
  }, []);

  // Remove dimension from chips
  const handleRemoveDimension = useCallback((field: string) => {
    setRowDimensions(prev => prev.filter(d => d.field !== field));
    setColumnDimensions(prev => prev.filter(d => d.field !== field));
  }, []);

  // For toolbar: first row dimension and first column dimension
  const primaryRowDim = rowDimensions[0] || null;
  const handlePrimaryRowDimChange = useCallback((dim: ReportDimension | null) => {
    if (dim) {
      setRowDimensions(prev => [dim, ...prev.slice(1)]);
    } else {
      setRowDimensions([]);
    }
    setActiveViewId(null);
  }, []);

  if (isLoadingMeta) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <ReportToolbar
        fields={fieldMetadata}
        viewType={viewType}
        onViewTypeChange={setViewType}
        chartType={chartType}
        onChartTypeChange={setChartType}
        rowDimension={primaryRowDim}
        onRowDimensionChange={handlePrimaryRowDimChange}
        stackDimension={stackDimension}
        onStackDimensionChange={setStackDimension}
        measures={measures}
        onMeasuresChange={setMeasures}
        chartSort={chartSort}
        onChartSortChange={setChartSort}
        onSave={() => setSaveDialogOpen(true)}
      />

      {/* Saved views bar */}
      <SavedViewsBar
        views={savedViews}
        onLoadView={handleLoadView}
        onDeleteView={handleDeleteView}
        activeViewId={activeViewId}
      />

      {/* Filter panel with active chips */}
      <AssetFilterPanel
        onAddDimension={handleAddDimension}
        savedViews={savedViews}
        onLoadView={handleLoadView}
        onDeleteView={handleDeleteView}
        rowDimensions={rowDimensions}
        columnDimensions={columnDimensions}
        onRemoveDimension={handleRemoveDimension}
        activeFilters={activeFilters}
        onAddFilter={(f) => setActiveFilters(prev => [...prev, f])}
        onRemoveFilter={(id) => setActiveFilters(prev => prev.filter(f => f.id !== id))}
        onReplaceFilter={(id, f) => setActiveFilters(prev => prev.map(existing => existing.id === id ? f : existing))}
      />

      {/* Report output */}
      {isLoadingReport ? (
        <Skeleton className="h-[400px] w-full rounded-lg" />
      ) : !reportData ? (
        <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
          Selecteer een dimensie en meetwaarde om de rapportage te starten
        </div>
      ) : viewType === 'chart' ? (
        <ChartView
          data={
            stackDimension
              ? pivotToStackedChart(reportData as PivotReportResponse)
              : reportData as ChartReportResponse
          }
          chartType={stackDimension ? 'stacked_bar' : chartType}
          sortOrder={chartSort}
        />
      ) : (
        <PivotTableView
          data={reportData as PivotReportResponse}
          rowDimensions={rowDimensions}
          columnDimensions={columnDimensions}
          fieldMetadata={fieldMetadata}
          onAddRowDimension={handleAddRowDimension}
          onRemoveRowDimension={handleRemoveRowDimension}
          onAddColumnDimension={handleAddColumnDimension}
          onRemoveColumnDimension={handleRemoveColumnDimension}
        />
      )}

      {/* Save dialog */}
      <SaveViewDialog
        isOpen={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveView}
      />
    </div>
  );
};

export default AssetStatisticsTab;
